import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { RECORD_PARAMS } from '../paths.js';
import { PAGE_TITLE_ID } from '../ui/PageHead.js';
import { scrollEntryKey, useScrollMemory } from './scroll-memory.js';

/**
 * Which page an address is: its path, and — where the path names its record in the query — that record
 * (`RECORD_PARAMS`). Any other query (a sort, a filter, a tab) leaves the page as it is.
 */
export function pageKeyOf(pathname: string, search: string): string {
  const param = RECORD_PARAMS[pathname];
  if (param === undefined) return pathname;
  return `${pathname}?${param}=${new URLSearchParams(search).get(param) ?? ''}`;
}

/**
 * On every route change: reset scroll to the top and move focus to the page heading (02-patterns §4). Back and Forward
 * return the reader to where they were on that entry — `useScrollMemory`, which restores once the list's rows have
 * landed (Phase 1c finding D21) — and focus still moves, so a screen-reader user is told where they are.
 */
export function RouteFocus() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const first = useRef(true);
  const pageKey = pageKeyOf(location.pathname, location.search);
  useScrollMemory(scrollEntryKey(location), pageKey, navigationType);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    // Why no scroll here: where the page lands — the top, or the entry's remembered place — is `useScrollMemory`'s.
    let attempts = 0;
    let frame = 0;
    const focusHeading = (): void => {
      const heading = document.getElementById(PAGE_TITLE_ID) ?? document.getElementById('main');
      if (heading) {
        heading.focus({ preventScroll: true });
        return;
      }
      // Why: a page may render its head a frame after the route commits; retry briefly, then give up.
      if (attempts < 10) {
        attempts += 1;
        frame = requestAnimationFrame(focusHeading);
      }
    };
    frame = requestAnimationFrame(focusHeading);
    return () => cancelAnimationFrame(frame);
    // Why: the page alone — its path and the record its query names (`pageKeyOf`) — so a sort or tab change in the query
    // never steals focus or scroll, while opening another record moves both (the stage 6 review: one report to another).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  return null;
}
